import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { EpisodePicker } from '@/components/player/EpisodePicker';
import { VideoPlayer } from '@/components/player/VideoPlayer';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState, StateMessage } from '@/components/ui/StateMessage';
import { useAnimeDetail } from '@/hooks/useAnimeQueries';
import { useLibrary } from '@/hooks/useLibrary';

export function WatchPage() {
	const { slug, episodeSlug } = useParams<{
		slug: string;
		episodeSlug?: string;
	}>();
	const detailQuery = useAnimeDetail(slug);
	const { recordHistory, isSaved, toggle } = useLibrary();
	const [serverIndex, setServerIndex] = useState(0);
	const navigate = useNavigate();

	const anime = detailQuery.data;

	/**
	 * Tập có thể tồn tại ở nhiều server. Ưu tiên server đang chọn, nếu không có
	 * tập đó thì nhảy sang server đầu tiên có.
	 */
	const resolved = useMemo(() => {
		if (!anime) return null;
		const servers = anime.servers;
		if (servers.length === 0) return null;

		if (!episodeSlug) {
			const firstServer = servers[0];
			const firstEpisode = firstServer?.episodes[0];
			return firstServer && firstEpisode
				? { serverIndex: 0, episode: firstEpisode }
				: null;
		}

		const preferred = servers[serverIndex]?.episodes.find(
			(episode) => episode.slug === episodeSlug,
		);
		if (preferred) return { serverIndex, episode: preferred };

		for (let index = 0; index < servers.length; index += 1) {
			const found = servers[index]?.episodes.find(
				(episode) => episode.slug === episodeSlug,
			);
			if (found) return { serverIndex: index, episode: found };
		}
		return null;
	}, [anime, episodeSlug, serverIndex]);

	const activeServer = anime?.servers[resolved?.serverIndex ?? 0];
	const episodeIndex = activeServer?.episodes.findIndex(
		(episode) => episode.slug === resolved?.episode.slug,
	);

	const previousEpisode =
		episodeIndex !== undefined && episodeIndex > 0
			? activeServer?.episodes[episodeIndex - 1]
			: undefined;
	const nextEpisode =
		episodeIndex !== undefined && episodeIndex >= 0
			? activeServer?.episodes[episodeIndex + 1]
			: undefined;

	useEffect(() => {
		if (!anime || !resolved) return;
		recordHistory(anime, {
			lastEpisodeSlug: resolved.episode.slug,
			lastEpisodeName: resolved.episode.name,
		});
		document.title = `${anime.title} · ${resolved.episode.name} — NekoStream`;
	}, [anime, resolved, recordHistory]);

	const handleProgress = useCallback(
		(percent: number) => {
			if (!anime || !resolved) return;
			recordHistory(anime, {
				lastEpisodeSlug: resolved.episode.slug,
				lastEpisodeName: resolved.episode.name,
				progress: percent,
			});
		},
		[anime, resolved, recordHistory],
	);

	if (detailQuery.isPending) {
		return (
			<div className="mt-6 grid gap-4">
				<Skeleton className="aspect-video w-full rounded-card" />
				<Skeleton className="h-7 w-1/2" />
				<Skeleton className="h-24 w-full" />
			</div>
		);
	}

	if (detailQuery.error || !anime) {
		return (
			<div className="mt-10">
				<ErrorState
					error={detailQuery.error ?? new Error('Không tìm thấy phim.')}
					onRetry={() => void detailQuery.refetch()}
				/>
			</div>
		);
	}

	if (anime.servers.length === 0) {
		return (
			<div className="mt-10">
				<StateMessage
					icon="🎬"
					title="Tựa này chưa có tập nào để phát"
					description="Kho phim chưa cập nhật link. Bạn có thể xem thông tin phim trong lúc chờ."
					action={
						<Link
							to={`/phim/${anime.slug}`}
							className="rounded-lg bg-accent px-4 py-2 text-sm font-bold text-white"
						>
							Xem chi tiết
						</Link>
					}
				/>
			</div>
		);
	}

	// Vào /xem/:slug không kèm tập, hoặc tập không tồn tại → chuẩn hoá về tập đầu.
	const fallbackEpisode = anime.servers[0]?.episodes[0];
	if (!resolved && fallbackEpisode) {
		return (
			<Navigate to={`/xem/${anime.slug}/${fallbackEpisode.slug}`} replace />
		);
	}
	if (!resolved) return null;

	if (!episodeSlug) {
		return (
			<Navigate to={`/xem/${anime.slug}/${resolved.episode.slug}`} replace />
		);
	}

	const inWatchlist = isSaved('watchlist', anime.slug);

	return (
		<>
			<nav className="mt-6 mb-3 flex flex-wrap items-center gap-2 text-sm text-muted">
				<Link to="/" className="hover:text-ink">
					Trang chủ
				</Link>
				<span aria-hidden="true">/</span>
				<Link to={`/phim/${anime.slug}`} className="hover:text-ink">
					{anime.title}
				</Link>
				<span aria-hidden="true">/</span>
				<span className="text-ink">{resolved.episode.name}</span>
			</nav>

			<VideoPlayer
				// key buộc player dựng lại sạch sẽ khi đổi tập/server.
				key={`${resolved.serverIndex}-${resolved.episode.slug}`}
				src={resolved.episode.m3u8}
				embedUrl={resolved.episode.embed}
				poster={anime.thumbUrl}
				title={`${anime.title} — ${resolved.episode.name}`}
				autoPlay
				onProgress={handleProgress}
			/>

			<div className="mt-4 flex flex-wrap items-center justify-between gap-3">
				<div className="min-w-0">
					<h1 className="truncate text-xl font-bold">{anime.title}</h1>
					<p className="text-sm text-muted">
						{resolved.episode.name}
						{activeServer ? ` · ${activeServer.name}` : ''}
						{anime.episodeCurrent ? ` · ${anime.episodeCurrent}` : ''}
					</p>
				</div>

				<div className="flex flex-wrap gap-2">
					{previousEpisode && (
						<Link
							to={`/xem/${anime.slug}/${previousEpisode.slug}`}
							className="rounded-lg border border-line px-3.5 py-2 text-sm text-muted transition hover:border-accent hover:text-ink"
						>
							‹ {previousEpisode.name}
						</Link>
					)}
					{nextEpisode && (
						<Link
							to={`/xem/${anime.slug}/${nextEpisode.slug}`}
							className="rounded-lg border border-accent bg-accent/15 px-3.5 py-2 text-sm font-bold transition hover:bg-accent/25"
						>
							{nextEpisode.name} ›
						</Link>
					)}
					<button
						type="button"
						onClick={() => toggle('watchlist', anime)}
						className="rounded-lg bg-white/10 px-3.5 py-2 text-sm font-bold transition active:scale-[0.97]"
					>
						{inWatchlist ? '✓ Đã lưu' : '＋ Danh sách'}
					</button>
				</div>
			</div>

			<section className="mt-8">
				<SectionHeader title="Chọn tập" />
				<EpisodePicker
					servers={anime.servers}
					activeServerIndex={resolved.serverIndex}
					onServerChange={(index) => {
						setServerIndex(index);
						// Giữ nguyên vị trí tập khi đổi server; slug giữa các server
						// không luôn giống nhau nên phải điều hướng theo thứ tự tập.
						const episodes = anime.servers[index]?.episodes ?? [];
						const target =
							episodes[episodeIndex ?? 0] ?? episodes[0];
						if (target) {
							navigate(`/xem/${anime.slug}/${target.slug}`, { replace: true });
						}
					}}
					activeEpisodeSlug={resolved.episode.slug}
					buildEpisodeHref={(nextSlug) => `/xem/${anime.slug}/${nextSlug}`}
				/>
			</section>

			{anime.synopsis && (
				<section className="mt-8">
					<SectionHeader title="Nội dung" />
					<p className="max-w-[80ch] text-[#c6c4d4]">{anime.synopsis}</p>
				</section>
			)}
		</>
	);
}
